import {
    useEffect,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type ViewStyle,
    Animated,
    Platform,
    View
} from "react-native";
import type ILoadingDotsProps from "./type";
import stylesheet, {
    LOADING_DOTS_WEB_LAYERS,
    getLoadingDotsMeasures,
    getLoadingDotsTones,
    getLoadingDotsSize,
    useStyles
} from "./stylesheet";
import {
    LOADING_DOTS_PATTERNS,
    LOADING_DOTS_MOTIONS
} from "./constant";
import {
    getLoadingDotsLayerRanges,
    getLoadingDotsCells
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    MOTION_EASINGS
} from "../../../../helpers/motion";

const LoadingDots: FC<ILoadingDotsProps> = ({
    "aria-hidden": ariaHidden,
    "aria-label": ariaLabel,
    pattern = "lattice",
    surface = "default",
    type = "neutral",
    size = "medium",
    customLocalize,
    customColor,
    customTheme,
    style,
    ...props
}) => {
    const {
        durations,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const progress = useRef(new Animated.Value(0)).current;

    const isWeb = Platform.OS === "web";

    const patternSpec = LOADING_DOTS_PATTERNS[pattern];

    const motionSpec = LOADING_DOTS_MOTIONS[patternSpec.motion];

    const cells = useMemo(() => getLoadingDotsCells({
        pattern
    }), [
        pattern
    ]);

    const currentSize = getLoadingDotsSize({
        iconSizes,
        size
    });

    const measures = getLoadingDotsMeasures({
        columns: patternSpec.columns,
        size: currentSize,
        radiuses,
        borders
    });

    const tones = getLoadingDotsTones({
        customColor,
        surface,
        colors,
        type
    });

    const loopDuration = durations[motionSpec.duration];

    const styles = useStyles({
        measures,
        tones
    });

    const phases = useMemo(() => {
        const result: Record<string, {
            dark: Animated.AnimatedInterpolation<number>;
            mid: Animated.AnimatedInterpolation<number>;
        }> = {};

        if(!loopDuration || isWeb) {
            return result;
        }

        const {
            inputRange,
            darkRange,
            midRange
        } = getLoadingDotsLayerRanges({
            motion: patternSpec.motion
        });

        cells.forEach((cell) => {
            if(cell.offset === null || result[cell.offset]) {
                return;
            }

            const phase = Animated.modulo(Animated.add(progress, cell.offset), 1);

            result[cell.offset] = {
                dark: phase.interpolate({
                    outputRange: darkRange,
                    extrapolate: "clamp",
                    inputRange
                }),
                mid: phase.interpolate({
                    outputRange: midRange,
                    extrapolate: "clamp",
                    inputRange
                })
            };
        });

        return result;
    }, [
        patternSpec.motion,
        loopDuration,
        progress,
        isWeb,
        cells
    ]);

    useEffect(() => {
        progress.setValue(0);

        if(!loopDuration || isWeb) {
            return;
        }

        const animation = Animated.loop(Animated.timing(progress, {
            easing: MOTION_EASINGS.linear,
            duration: loopDuration,
            useNativeDriver: true,
            isInteraction: false,
            toValue: 1
        }));

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        loopDuration,
        progress,
        isWeb
    ]);

    const renderDot = (cell: (typeof cells)[number], index: number) => {
        if(isWeb && loopDuration && cell.offset !== null) {
            const webLayers = LOADING_DOTS_WEB_LAYERS[patternSpec.motion];

            const webAnimationDynamicStyle = {
                animationDelay: `${-cell.offset * loopDuration}ms`,
                animationDuration: `${loopDuration}ms`
            } as ViewStyle;

            return <View
                style={[
                    stylesheet.dot,
                    styles.dot
                ]}
                key={index}
            >
                <View
                    style={[
                        stylesheet.layer,
                        styles.mid,
                        webLayers.mid,
                        webAnimationDynamicStyle
                    ]}
                />
                <View
                    style={[
                        stylesheet.layer,
                        styles.dark,
                        webLayers.dark,
                        webAnimationDynamicStyle
                    ]}
                />
            </View>;
        }

        const phase = cell.offset === null ? undefined : phases[cell.offset];

        if(!phase) {
            return <View
                style={[
                    stylesheet.dot,
                    styles.dot,
                    !loopDuration && cell.isLead && styles.dark
                ]}
                key={index}
            />;
        }

        return <View
            style={[
                stylesheet.dot,
                styles.dot
            ]}
            key={index}
        >
            <Animated.View
                style={[
                    stylesheet.layer,
                    styles.mid,
                    {
                        opacity: phase.mid
                    }
                ]}
            />
            <Animated.View
                style={[
                    stylesheet.layer,
                    styles.dark,
                    {
                        opacity: phase.dark
                    }
                ]}
            />
        </View>;
    };

    return <View
        {...props}
        aria-label={ariaHidden ? undefined : ariaLabel ?? localize("loading")}
        role={ariaHidden ? undefined : "status"}
        style={[
            style,
            stylesheet.container,
            styles.container
        ]}
        aria-hidden={ariaHidden}
    >
        {cells.map(renderDot)}
    </View>;
};
export default LoadingDots;
